import Cookies from 'js-cookie';

import {
  DEFAULT_CURRENCY,
  GEOLOCATE_API_ENDPOINT,
  REGION_COUNTRIES,
  REGION_ZONES,
  SYMBOL_CURRENCIES
} from '../constants';
import { isBrowser } from './misc';

const cookieExpiresMillis = 45 * 60 * 1000; // 45 minutes
const countryRegionMap = {};

Object.keys(REGION_COUNTRIES).forEach((region) => {
  REGION_COUNTRIES[region].forEach((country) => {
    countryRegionMap[country] = region;
  });
});

export const getCountry = async () => {
  const countryCodeCookie = Cookies.get('__Host-country');

  if (countryCodeCookie !== undefined) {
    console.log('countryCodeCookie', countryCodeCookie);
    return countryCodeCookie;
  }

  try {
    const response = await fetch(GEOLOCATE_API_ENDPOINT);
    const { data } = await response.json();
    const { country_code: countryCode } = data;

    const countryCodeLower = countryCode.toLowerCase();
    const expirationDate = new Date(new Date().getTime() + cookieExpiresMillis);

    Cookies.set(
      '__Host-country',
      countryCodeLower,
      { expires: expirationDate, path: '/', secure: true, sameSite: 'strict' }
    );

    return countryCodeLower;
  } catch (error) {
    return null;
  }
};

export const getRegion = (country) => {
  if (!country || countryRegionMap[country] === undefined) {
    return 'default';
  }

  return countryRegionMap[country];
};

export const getRegionZone = (region) => {
  if (region === null || REGION_ZONES[region] === undefined) {
    return REGION_ZONES.default;
  }

  return REGION_ZONES[region];
};

export const getDeveloperRegions = (region, country) => {
  const devRegions = [];
  const americas = ['ag', 'ai', 'an', 'ar', 'aw', 'bb', 'bl', 'bm', 'bo', 'bq', 'br', 'bs', 'bz', 'ca', 'cl', 'co', 'cr', 'cu', 'cw', 'dm', 'do', 'ec', 'gd', 'gf', 'gp', 'gt', 'gy', 'hn', 'ht', 'jm', 'kn', 'ky', 'lc', 'mf', 'mq', 'ms', 'mx', 'ni', 'pa', 'pe', 'pm', 'pr', 'py', 'sr', 'sv', 'sx', 'tc', 'tt', 'us', 'uy', 'vc', 've', 'vg', 'vi'];
  const apacCountries = ['as', 'au', 'bd', 'bn', 'bt', 'cc', 'cn', 'cx', 'fj', 'fm', 'gu', 'hk', 'id', 'in', 'io', 'jp', 'kh', 'ki', 'kp', 'kr', 'la', 'lk', 'mh', 'mm', 'mn', 'mo', 'mp', 'mv', 'my', 'nf', 'np', 'nr', 'nu', 'nz', 'pg', 'ph', 'pk', 'pn', 'pw', 'sb', 'sg', 'th', 'to', 'tv', 'tw', 'um', 'vn', 'vu', 'ws'];
  const isApac = apacCountries.includes(country);
  // Not US/Canada/Britain/Japan/India
  const isEmea = !['default', 'canada', 'gbp', 'jpy', 'india'].includes(region);

  // Europe + France + Switzerland + Liechtenstein -> EU Region
  if (['eu', 'fr', 'chf', 'sek', 'dkk', 'nok'].includes(region)) {
    devRegions.push('eu');
  }
  // American countries (North + South)
  if (americas.includes(country)) {
    devRegions.push('americas');
  }
  // APAC + EMEA + Great Britain/UK
  if (isApac || isEmea || country === 'gb') {
    devRegions.push('apac-emea-gb');
  }
  // APAC countries
  if (isApac) {
    devRegions.push('apac-new');
  }
  // Not US/Canada/Britain/Japan/India
  if (isEmea) {
    devRegions.push('emea');
  }
  // Not US/Canada/Japan/India
  if (isEmea || country === 'gb') {
    devRegions.push('emea-gb');
  }
  // EMEA only (No APAC): Europe + Middle East + Africa
  if (!isApac && isEmea) {
    devRegions.push('emea-gb-new');
  }
  // US + EMEA + Japan + India
  if (['default', 'emea', 'jpy', 'india'].includes(region)) {
    devRegions.push('usd');
  }
  // US + Canada + India
  if (['default', 'canada', 'india'].includes(region)) {
    devRegions.push('srs');
  }
  // Britain + Germany
  if (country === 'gb' || country === 'de') {
    devRegions.push('gb-de');
  }
  // Canada + US
  if (country === 'ca' || country === 'us') {
    devRegions.push('ca-us');
  }
  // India
  if (country === 'in') {
    devRegions.push('india');
  }
  // Germany
  if (country === 'de') {
    devRegions.push('de');
  }
  // France
  if (country === 'fr') {
    devRegions.push('fr');
  }
  // Spain
  if (country === 'es') {
    devRegions.push('es');
  }
  // Great Britain / UK
  if (country === 'gb') {
    devRegions.push('gb');
  }
  // China
  if (country === 'cn') {
    devRegions.push('cn');
  }
  // Taiwan
  if (country === 'tw') {
    devRegions.push('tw');
  }
  return devRegions;
};

export const getPhoneNumbers = (region, country) => {
  const countriesWithOnePhone = ['au', 'nz', 'ca', 'gb', 'nl', 'de', 'cn'];
  let salesPhone = '+1.408.886.7177';
  let supportPhone = '+1.408.610.1631';

  if (country === 'fr') {
    salesPhone = '+33 (0) 1 85 14 97 32';
    supportPhone = '+33 (0) 1 85 14 97 58';
  } else if (country === 'au') {
    salesPhone = '+61 (2) 9161 7275';
  } else if (country === 'nz') {
    salesPhone = '+64 (9) 886 1323';
  } else if (country === 'ca') {
    salesPhone = '(778) 569-0889';
  } else if (country === 'gb') {
    salesPhone = '+44 (0) 20 3884 2681';
  } else if (country === 'de') {
    salesPhone = '+49 (0) 800 18 33 165';
  } else if (country === 'nl' || region === 'eu') {
    salesPhone = '+31 (0) 20 888 5115';
  } else if (country === 'cn') {
    salesPhone = '+86 (0) 571 8711 9188';
  }

  if (countriesWithOnePhone.includes(country)) {
    supportPhone = null;
  }

  return { salesPhone, supportPhone };
};

export const getCurrencyFromPrice = (price) => {
  // Remove all numbers, commas, decimals, and whitespace characters
  const currencySymbol = price?.replace(/[0-9,. \\"]/g, '');

  if (currencySymbol === '' || SYMBOL_CURRENCIES[currencySymbol] === undefined) {
    return DEFAULT_CURRENCY;
  }

  return SYMBOL_CURRENCIES[currencySymbol];
};

export const applyPricingFormat = (language, country, price) => {
  const currency = getCurrencyFromPrice(price); // eg: USD, MXN, etc.
  const locale = `${language}-${country.toUpperCase()}`; // get locale - enable param overrides
  const rawPrice = price?.replace(/[^0-9.]/g, '');
  const formatter = new Intl.NumberFormat(
    isBrowser ? locale : 'en', // where locale is now, code had 'default' or undefined // for US browser, this messed up formatting
    {
      currency,
      style: 'currency',
      minimumFractionDigits: 2,
      // https://github.com/nodejs/node/issues/41568
      // Node < 19 doesn't support trailingZeroDisplay option so we need to
      // strip it manually below otherwise SSR gives 5.00 instead of 5 and causes
      // hydration mismatch
      trailingZeroDisplay: 'stripIfInteger'
    }
  );

  const formatted = formatter.format(rawPrice);

  if (!isBrowser) {
    // remove trailing zeros if integer - this works because SSR renders EN locale
    return formatted.replace(/\.00$/, '');
  }

  return formatted;
};
